import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import metadata from "@/seo/routeMetadata.json";

const publicProductEntryPaths = new Set(["/assessment", "/coaching", "/app"]);

const normalizePath = (pathname = "/") => {
  const withoutQuery = pathname.split("?")[0].split("#")[0] || "/";
  if (withoutQuery === "/") return "/";
  return withoutQuery.replace(/\/+$/, "");
};

export const findRouteMetadata = (pathname) => {
  const normalized = normalizePath(pathname);
  const exact = metadata.routes.find((route) => route.path === normalized);
  if (exact) return exact;

  return metadata.routes.find(
    (route) =>
      route.prefix &&
      (normalized === route.path || normalized.startsWith(`${route.path}/`)),
  );
};

export const canonicalForRoute = (route) => {
  const origin = route?.origin === "product"
    ? metadata.site.productOrigin
    : route?.origin === "public"
      ? metadata.site.publicOrigin
      : route?.index === false
        ? metadata.site.productOrigin
        : metadata.site.publicOrigin;
  const routePath = route?.path === "/" ? "" : route?.path || "";
  const suffix = publicProductEntryPaths.has(route?.path) ? "/" : "";
  return `${origin}${routePath}${suffix}`;
};

const upsertMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    element.dataset.seoManaged = "true";
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => {
    element.setAttribute(name, value);
  });
};

const upsertCanonical = (href) => {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    element.dataset.seoManaged = "true";
    document.head.appendChild(element);
  }
  element.href = href;
};

const breadcrumbGraph = (route) => {
  if (!route.breadcrumbs?.length) return null;

  const parts = route.path.split("/").filter(Boolean);
  return {
    "@type": "BreadcrumbList",
    "@id": `${canonicalForRoute(route)}#breadcrumb`,
    itemListElement: route.breadcrumbs.map((name, index) => {
      const isHome = index === 0;
      const itemPath = isHome ? "" : `/${parts.slice(0, index).join("/")}`;
      return {
        "@type": "ListItem",
        position: index + 1,
        name,
        item: `${metadata.site.publicOrigin}${itemPath}`,
      };
    }),
  };
};

export const structuredDataForRoute = (route) => {
  if (!route || route.index === false) return null;
  const canonical = canonicalForRoute(route);
  const graph = [
    {
      "@type": "Organization",
      "@id": `${metadata.site.publicOrigin}/#organization`,
      name: metadata.site.organization,
      url: metadata.site.publicOrigin,
      logo: {
        "@type": "ImageObject",
        url: `${metadata.site.publicOrigin}/adaptive-companion-icon.svg`,
        width: 512,
        height: 512,
      },
      brand: {
        "@type": "Brand",
        name: metadata.site.name,
      },
    },
    {
      "@type": "WebSite",
      "@id": `${metadata.site.publicOrigin}/#website`,
      url: metadata.site.publicOrigin,
      name: `${metadata.site.name} by ${metadata.site.organization}`,
      publisher: { "@id": `${metadata.site.publicOrigin}/#organization` },
      inLanguage: "en-CA",
    },
    {
      "@type": route.schemaType || "WebPage",
      "@id": `${canonical}#webpage`,
      url: canonical,
      name: route.title,
      headline: route.heading,
      description: route.description,
      isPartOf: { "@id": `${metadata.site.publicOrigin}/#website` },
      about: {
        "@type": "Thing",
        name: metadata.site.name,
        description:
          "A science-informed methodology developed by FORR Health that connects physiological state, nervous-system responses, recovery, environment, and behaviour.",
      },
      inLanguage: "en-CA",
    },
  ];

  const breadcrumbs = breadcrumbGraph(route);
  if (breadcrumbs) graph.push(breadcrumbs);

  return { "@context": "https://schema.org", "@graph": graph };
};

const SeoRoute = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const route = findRouteMetadata(pathname);
    const fallback = {
      title: "Page not found | Adaptive Physiology",
      description: "The requested Adaptive Physiology page could not be found.",
      index: false,
      path: normalizePath(pathname),
    };
    const active = route || fallback;
    const canonical = canonicalForRoute(active);
    const robots = active.index === false
      ? "noindex, nofollow, noarchive"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

    document.title = active.title;
    upsertMeta('meta[name="description"]', {
      name: "description",
      content: active.description,
    });
    upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    upsertMeta('meta[name="googlebot"]', { name: "googlebot", content: robots });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: active.title });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: active.description,
    });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: `${metadata.site.name} by ${metadata.site.organization}`,
    });
    upsertMeta('meta[property="og:locale"]', {
      property: "og:locale",
      content: metadata.site.locale,
    });
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: metadata.site.defaultImage,
    });
    upsertMeta('meta[property="og:image:width"]', {
      property: "og:image:width",
      content: "1200",
    });
    upsertMeta('meta[property="og:image:height"]', {
      property: "og:image:height",
      content: "630",
    });
    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: active.title,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: active.description,
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: metadata.site.defaultImage,
    });
    upsertCanonical(canonical);

    document.head
      .querySelectorAll('script[data-seo-json-ld="true"]')
      .forEach((element) => element.remove());
    const structuredData = structuredDataForRoute(route);
    if (structuredData) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seoJsonLd = "true";
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [pathname]);

  return null;
};

export default SeoRoute;
